import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { BAD_EMAIL, BAD_FEE, REQUIRED, SUBMIT_FAIL, apiFieldErrors, blank, cleanFee, feeBad, scrollToFirstError, submitErrorMessage } from '@/lib/formErrors';

import { useToast } from '@/components/Toast';
import { TemplateControls } from '@/components/TemplateControls';
import { useEngagement, useTemplateControls, useUpdateEngagement, useLookup } from '@/api/hooks';
import { api } from '@/api/client';
import { Section, Field, FeeHint, Warn, LookupCombo, EMAIL_RE } from '@/components/FormKit';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

/**
 * Recurring Annually is stored as "1"/"0" (what Power Apps writes). Some older
 * rows created by this app hold "Yes"/"No", so accept both and map to the
 * Yes/No the dropdown shows.
 */
function recurringToUi(v: unknown): string {
  const s = String(v ?? '').trim().toLowerCase();
  if (s === '1' || s === 'yes' || s === 'true') return 'Yes';
  if (s === '0' || s === 'no' || s === 'false') return 'No';
  return '';
}
// [record key, state key] for the editable engagement-detail text/email fields.
const DETAIL_FIELDS: [string, string][] = [
  ['PracticeAreaPartner', 'practiceAreaPartner'],
  ['TaxSigner', 'taxSigner'],
  ['EngagementManager', 'engagementManager'],
  ['InCharge', 'inCharge'],
  ['EngagementLetterFee', 'engagementLetterFee'],
  ['EngagementLocation', 'engagementLocation'],
  ['EmailForInvoice', 'emailForInvoice'],
  ['EmailForMonthlyStatements', 'emailForMonthlyStatements'],
  ['ELSignerName', 'elSignerName'],
  ['ELSignerEmail', 'elSignerEmail'],
];

export function EngagementEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const notify = useToast();
  const rec = useEngagement(id);
  const r = rec.data;
  const update = useUpdateEngagement(id ?? '');
  const [editMode, setEditMode] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [apiErrs, setApiErrs] = useState<Record<string, string>>({});

  const serviceCategory = String(r?.ServiceCategory ?? '');
  const templateName = String(r?.TemplateName ?? '');
  const controlsQ = useTemplateControls(serviceCategory || undefined, templateName || undefined);
  const controls = controlsQ.data ?? [];

  // editable header + details
  const [year, setYear] = useState('');
  const [fiscalYearEndMonth, setFiscalYearEndMonth] = useState('');
  const [recurringAnnually, setRecurringAnnually] = useState(''); // '' | 'Yes' | 'No'
  const [d, setD] = useState<Record<string, string>>({});
  const setField = (k: string, v: string) => setD((s) => ({ ...s, [k]: v }));
  const [deliverables, setDeliverables] = useState<Record<string, boolean>>({});
  const [otherDetails, setOtherDetails] = useState<Record<string, string>>({});
  const [counties, setCounties] = useState('');

  const years = useLookup('fiscal-years');
  const partners = useLookup('practice-area-partners');
  const managers = useLookup('engagement-managers');
  const signers = useLookup('tax-signers');
  const inCharge = useLookup('in-charge');

  // initialize from the saved record + controls
  useEffect(() => {
    if (!r) return;
    setYear(String(r.Year ?? ''));
    setFiscalYearEndMonth(String(r.FiscalYearEndMonth ?? ''));
    setRecurringAnnually(recurringToUi(r.RecurringAnnually));
    const dd: Record<string, string> = {};
    // Maconomy stores a blank text field as a single space: load those as empty.
    for (const [rk, sk] of DETAIL_FIELDS) { const v = String(r[rk] ?? ''); dd[sk] = v.trim() ? v : ''; }
    dd.notes = String(r.Notes ?? '');
    setD(dd);
    const del: Record<string, boolean> = {};
    const od: Record<string, string> = {};
    for (const c of controls) {
      if (c.ControlType === 'Deliverable') del[c.ControlKey] = r[c.ControlKey] === 1 || r[c.ControlKey] === true;
      // Counties has its own field (`counties`, sent as propertyTaxCountries); keep it out
      // of otherDetails so it isn't sent twice.
      else if (c.ControlKey !== 'PropertyTaxCountries') {
        // vw_Engagement_Base exposes State Incorporated as StateIncorporated1.
        const v = r[c.ControlKey] ?? (c.ControlKey === 'StateIncorporated' ? r.StateIncorporated1 : undefined);
        od[c.ControlKey] = c.InputKind === 'date' && v ? String(v).slice(0, 10) : String(v ?? '');
      }
    }
    setDeliverables(del);
    setOtherDetails(od);
    setCounties(String(r.PropertyTaxCountries ?? ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [r, controlsQ.data]);

  // Engagement Location derives from Practice Area Partner (edit mode only).
  const engLoc = useQuery({
    queryKey: ['eng-location', d.practiceAreaPartner],
    queryFn: () => api.get<{ EngLocation: string }[]>(`/lookups/engagement-locations?partner=${encodeURIComponent(d.practiceAreaPartner)}`),
    enabled: editMode && !!d.practiceAreaPartner,
  });
  useEffect(() => {
    if (editMode) setField('engagementLocation', String(engLoc.data?.[0]?.EngLocation ?? ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engLoc.data]);

  if (rec.isFetching && !r) return <div className="p-6">Loading…</div>;
  if (!r) return <div className="p-6">Engagement not found.</div>;

  const opType = r.CompletionDate || r.Source === 'Maconomy' ? 'Complete' : 'Pending';
  const D = !editMode;

  // The Power App's Edit dropdown stores a misspelled "Febraury", so completed/edited
  // records can hold a month that isn't in our (correctly spelled) list. Surface any such
  // stored value as an extra option so it displays and round-trips unchanged instead of
  // silently blanking (and losing the value on save).
  const monthOptions =
    fiscalYearEndMonth && !MONTHS.includes(fiscalYearEndMonth) ? [fiscalYearEndMonth, ...MONTHS] : MONTHS;

  // validation (mirrors the Power Apps Submit gate)
  const invoiceBad = !blank(d.emailForInvoice) && !EMAIL_RE.test(String(d.emailForInvoice).trim());
  const monthlyBad = !blank(d.emailForMonthlyStatements) && !EMAIL_RE.test(String(d.emailForMonthlyStatements).trim());
  const elEmailBad = !blank(d.elSignerEmail) && !EMAIL_RE.test(String(d.elSignerEmail).trim());
  // Change form (CAD Cleanup spec): NO deliverable is required to submit (A1) and the
  // type-specific "Other Details" fields are optional (A3) — those are captured at setup
  // and shouldn't be re-required on a change. Only the core header/assignment/billing
  // fields (+ email format) still gate submit. Deliverables are hidden on this screen (A2),
  // so their saved values just round-trip unchanged.

  // Highlight the fields blocking a submit (shown after the first Submit attempt).
  const errs: Record<string, string> = {};
  if (blank(year)) errs.year = REQUIRED;
  if (blank(fiscalYearEndMonth)) errs.fiscalYearEndMonth = REQUIRED;
  // Engagement Letter Fee is optional here (legacy engagements); still required on New Engagement.
  for (const k of ['practiceAreaPartner', 'taxSigner', 'engagementManager', 'emailForInvoice',
    'emailForMonthlyStatements', 'elSignerName', 'elSignerEmail']) if (blank(d[k])) errs[k] = REQUIRED;
  if (!errs.emailForInvoice && invoiceBad) errs.emailForInvoice = BAD_EMAIL;
  if (!errs.emailForMonthlyStatements && monthlyBad) errs.emailForMonthlyStatements = BAD_EMAIL;
  if (!errs.elSignerEmail && elEmailBad) errs.elSignerEmail = BAD_EMAIL;
  const feeIsBad = feeBad(d.engagementLetterFee);
  if (feeIsBad) errs.engagementLetterFee = BAD_FEE;
  const E: Record<string, string | undefined> = showErrors ? { ...apiErrs, ...errs } : apiErrs;

  const invalid =
    !year || !fiscalYearEndMonth || !d.practiceAreaPartner || !d.taxSigner || !d.engagementManager ||
    !d.emailForInvoice?.trim() || !d.emailForMonthlyStatements?.trim() ||
    !d.elSignerName?.trim() || !d.elSignerEmail?.trim() ||
    invoiceBad || monthlyBad || elEmailBad || feeIsBad;

  /** Submit: highlight what's missing before asking for confirmation. */
  function attempt() {
    setApiErrs({});
    if (invalid || Object.keys(errs).length) {
      setShowErrors(true);
      notify('error', SUBMIT_FAIL);
      scrollToFirstError();
      return;
    }
    setConfirm(true);
  }

  async function save() {
    setConfirm(false);
    const body = {
      serviceCategory, templateName, year: Number(year), fiscalYearEndMonth, recurringAnnually,
      clientId: String(r!.ClientID ?? ''),
      clientName: String(r!.ClientName ?? ''),
      clientManager: String(r!.ClientManager ?? ''),
      brcIndustry: String(r!.BRCIndustry ?? ''),
      ...d,
      engagementLetterFee: cleanFee(d.engagementLetterFee),
      deliverables, propertyTaxCountries: counties, otherDetails,
    };
    try {
      await update.mutateAsync({ body, opType });
      notify('success', 'Engagement change submitted (new pending request created).');
      navigate('/engagements');
    } catch (e) {
      setApiErrs(apiFieldErrors(e));
      setShowErrors(true);
      notify('error', submitErrorMessage(e));
      scrollToFirstError();
    }
  }

  return (
    <div className="w-full space-y-4 p-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">{String(r.ClientName || `Engagement ${r.EngagementID}`)}</h2>
          <p className="text-xs text-muted-foreground">Engagement {String(r.EngagementID ?? '')} · {templateName || serviceCategory}</p>
        </div>
        <div className="flex gap-2">
          <Link to={`/engagements/${id}/additional`} className="btn-secondary">Additional Details</Link>
          {!editMode ? (
            <button className="btn-primary" onClick={() => setEditMode(true)}>Edit Details</button>
          ) : (
            <>
              <button className="btn-secondary" onClick={() => { setEditMode(false); rec.refetch(); }}>Cancel</button>
              <button className="btn-success" disabled={update.isPending} onClick={attempt}>Submit</button>
            </>
          )}
        </div>
      </div>
      {editMode && (
        <div className="rounded-md border border-warning/40 bg-warning/10 p-2 text-xs">
          Saving creates a <b>new Pending Request</b> (the completed/original record is not modified).
        </div>
      )}

      <div className="columns-1 gap-4 xl:columns-2 [&>*]:mb-4 [&>*]:break-inside-avoid">
        <Section title="Engagement">
          <Field label="Service Category"><input className="input" value={serviceCategory} disabled /></Field>
          <Field label="Template Name"><input className="input" value={templateName} disabled /></Field>
          <Field error={E.year} label="Fiscal Year End *">
            <LookupCombo data={years.data} field="FiscalYear" value={year} onChange={setYear} disabled={D} placeholder="Select year…" />
          </Field>
          <Field error={E.fiscalYearEndMonth} label="Fiscal Year End Month *">
            <select className="input" disabled={D} value={fiscalYearEndMonth} onChange={(e) => setFiscalYearEndMonth(e.target.value)}>
              <option value="">— select —</option>{monthOptions.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </Field>
          <Field label="Recurring Annually">
            <select className="input" disabled={D} value={recurringAnnually} onChange={(e) => setRecurringAnnually(e.target.value)}>
              <option value="">— select —</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          </Field>
        </Section>

        <Section title="Client">
          <Field label="Client ID"><input className="input" value={String(r.ClientID ?? '')} disabled /></Field>
          <Field label="Client Name"><input className="input" value={String(r.ClientName ?? '')} disabled /></Field>
          <Field label="Contact Name"><input className="input" value={String(r.ContactName ?? '')} disabled /></Field>
          <Field label="Client Email"><input className="input" value={String(r.ClientEmail ?? '')} disabled /></Field>
          <Field label="Client Manager"><input className="input" value={String(r.ClientManager ?? '')} disabled /></Field>
          <Field label="BRC Industry"><input className="input" value={String(r.BRCIndustry ?? '')} disabled /></Field>
        </Section>

        <Section title="Assignments">
          <Field error={E.practiceAreaPartner} label="Practice Area Partner *">
            <LookupCombo data={partners.data} field="Name" value={d.practiceAreaPartner ?? ''}
              onChange={(v) => setField('practiceAreaPartner', v)} disabled={D} placeholder="Select partner…" />
          </Field>
          <Field label="Engagement Location">
            <input className="input" value={d.engagementLocation ?? ''} disabled placeholder="Set by Practice Area Partner" />
          </Field>
          <Field error={E.taxSigner} label="Tax Signer *">
            <LookupCombo data={signers.data} field="DisplayText" value={d.taxSigner ?? ''}
              onChange={(v) => setField('taxSigner', v)} disabled={D} placeholder="Select signer…" />
          </Field>
          <Field error={E.engagementManager} label="Engagement Manager *">
            <LookupCombo data={managers.data} field="EngagementManager" value={d.engagementManager ?? ''}
              onChange={(v) => setField('engagementManager', v)} disabled={D} placeholder="Select manager…" />
          </Field>
          <Field label="In Charge">
            <LookupCombo data={inCharge.data} field="InCharge" value={d.inCharge ?? ''}
              onChange={(v) => setField('inCharge', v)} disabled={D} placeholder="Select…" />
          </Field>
        </Section>

        <Section title="Billing & Engagement Letter">
          <Field error={E.engagementLetterFee} label="Engagement Letter Fee">
            <input className="input" disabled={D} value={d.engagementLetterFee ?? ''} onChange={(e) => setField('engagementLetterFee', e.target.value)} />
            {!D && !E.engagementLetterFee && <FeeHint bad={feeIsBad} />}
          </Field>
          <Field error={E.emailForInvoice} label={<>Email for Invoice * <Warn show={invoiceBad} tip="Enter a valid email" /></>}>
            <input className="input" disabled={D} value={d.emailForInvoice ?? ''} onChange={(e) => setField('emailForInvoice', e.target.value)} />
          </Field>
          <Field error={E.emailForMonthlyStatements} label={<>Email for Monthly Statements * <Warn show={monthlyBad} tip="Enter a valid email" /></>}>
            <input className="input" disabled={D} value={d.emailForMonthlyStatements ?? ''} onChange={(e) => setField('emailForMonthlyStatements', e.target.value)} />
          </Field>
          <Field error={E.elSignerName} label="EL Signer Name *">
            <input className="input" disabled={D} value={d.elSignerName ?? ''} onChange={(e) => setField('elSignerName', e.target.value)} />
          </Field>
          <Field error={E.elSignerEmail} label={<>EL Signer Email * <Warn show={elEmailBad} tip="Enter a valid email" /></>}>
            <input className="input" disabled={D} value={d.elSignerEmail ?? ''} onChange={(e) => setField('elSignerEmail', e.target.value)} />
          </Field>
        </Section>
      </div>

      <div>
        <label className="label">Notes</label>
        <textarea className="input" rows={2} disabled={D} value={d.notes ?? ''} onChange={(e) => setField('notes', e.target.value)} />
      </div>

      {/* Section 2 — DB-driven controls (pre-filled with saved values) */}
      {controlsQ.isFetching ? (
        <div className="card text-sm text-muted-foreground">Loading template controls…</div>
      ) : (
        <TemplateControls
          controls={controls}
          deliverables={deliverables}
          setDeliverable={(k, v) => setDeliverables((s) => ({ ...s, [k]: v }))}
          otherDetails={otherDetails}
          setOtherDetail={(k, v) => setOtherDetails((s) => ({ ...s, [k]: v }))}
          propertyTaxCountries={counties}
          setPropertyTaxCountries={setCounties}
          clientId={r.ClientID ? String(r.ClientID) : undefined}
          disabled={D}
          changeForm
          textKeys={templateName === 'Foreign Corporation Tax' ? new Set(['StateIncorporated']) : undefined}
          hideKeys={templateName === 'Gift Tax' || templateName === 'Gift Tax Dec' ? new Set(['FiduciaryName', 'FiduciaryTitle']) : undefined}
        />
      )}

      {editMode && <p className="text-xs font-semibold text-accent">*  Mandatory Fields</p>}

      <ConfirmDialog open={confirm}
        message="Do you wish to submit the engagement form changes? Press 'Yes' to continue, 'No' to cancel."
        onConfirm={save} onCancel={() => setConfirm(false)} />
    </div>
  );
}
